import React from 'react';
import { CalendarOff, Check, Star, Tag, UserX, X } from 'lucide-react';
import { MobileFullScreenModal, ModalDoneFooter } from '../../../ui/MobileFullScreenModal';
import { UserFilter } from '../../../dashboard/types/dashboardTypes';

interface MobileTaskStatusFilterProps {
  isOpen: boolean;
  onClose: () => void;
  showUnscheduledOnly: boolean;
  toggleShowUnscheduledOnly: () => void;
  showUnassignedOnly: boolean;
  toggleShowUnassignedOnly: () => void;
  showHighlightedOnly?: boolean;
  toggleShowHighlightedOnly?: () => void;
  hasCollaborators: boolean;
  availableTags: string[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

/**
 * Mobile status filter dropdown for tasks
 * Displays unscheduled/unassigned filters and tag filters in a full-screen modal
 */
export function MobileTaskStatusFilter({
  isOpen,
  onClose,
  showUnscheduledOnly,
  toggleShowUnscheduledOnly,
  showUnassignedOnly,
  toggleShowUnassignedOnly,
  showHighlightedOnly = false,
  toggleShowHighlightedOnly = () => {},
  hasCollaborators,
  availableTags,
  selectedTags,
  onTagsChange,
}: MobileTaskStatusFilterProps) {
  if (!isOpen) return null;

  return (
    <MobileFullScreenModal
      isOpen={isOpen}
      onClose={onClose}
      title="Task Filters"
      footerContent={<ModalDoneFooter onClose={onClose} />}
    >
      {/* Status section */}
      <div>
        <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
          <h3 className="font-medium text-gray-700">Status Filters</h3>
        </div>

        {/* Highlighted filter */}
        <div
          className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-[var(--content-bg-color)] cursor-pointer"
          onClick={toggleShowHighlightedOnly}
        >
          <div className="flex-1 flex items-center gap-2">
            <Star size={16} className="text-blue-500" />
            <span className="text-base">Highlighted Tasks</span>
          </div>
          <div
            className={`w-5 h-5 flex items-center justify-center rounded-full border ${
              showHighlightedOnly ? 'bg-green-500 border-green-500' : 'border-gray-300'
            }`}
          >
            {showHighlightedOnly && <Check size={14} className="text-white" />}
          </div>
        </div>

        {/* Unscheduled filter */}
        <div
          className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-[var(--content-bg-color)] cursor-pointer"
          onClick={toggleShowUnscheduledOnly}
        >
          <div className="flex-1 flex items-center gap-2">
            <CalendarOff size={16} className="text-blue-500" />
            <span className="text-base">Unscheduled Tasks</span>
          </div>
          <div
            className={`w-5 h-5 flex items-center justify-center rounded-full border ${
              showUnscheduledOnly ? 'bg-green-500 border-green-500' : 'border-gray-300'
            }`}
          >
            {showUnscheduledOnly && <Check size={14} className="text-white" />}
          </div>
        </div>

        {/* Unassigned filter - only show if has collaborators */}
        {hasCollaborators && (
          <div
            className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-[var(--content-bg-color)] cursor-pointer"
            onClick={toggleShowUnassignedOnly}
          >
            <div className="flex-1 flex items-center gap-2">
              <UserX size={16} className="text-blue-500" />
              <span className="text-base">Unassigned Tasks</span>
            </div>
            <div
              className={`w-5 h-5 flex items-center justify-center rounded-full border ${
                showUnassignedOnly ? 'bg-green-500 border-green-500' : 'border-gray-300'
              }`}
            >
              {showUnassignedOnly && <Check size={14} className="text-white" />}
            </div>
          </div>
        )}
      </div>

      {/* Tags section - if available */}
      {availableTags.length > 0 && (
        <div className="border-t border-gray-100">
          <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
            <h3 className="font-medium text-gray-700">Tags</h3>
            {selectedTags.length > 0 && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onTagsChange([]);
                }}
                className="text-blue-400 hover:text-blue-600 transition-colors"
                aria-label="Clear tag selection"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {availableTags.map(tag => (
            <div
              key={tag}
              className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-[var(--content-bg-color)] cursor-pointer"
              onClick={() => {
                if (selectedTags.includes(tag)) {
                  onTagsChange(selectedTags.filter(t => t !== tag));
                } else {
                  onTagsChange([...selectedTags, tag]);
                }
              }}
            >
              <div className="flex-1 truncate pr-6 flex items-center gap-2">
                <Tag size={16} className="text-blue-500 flex-shrink-0" />
                <span className="text-base">{tag}</span>
              </div>
              <div
                className={`w-5 h-5 flex items-center justify-center rounded-full border ${
                  selectedTags.includes(tag) ? 'bg-green-500 border-green-500' : 'border-gray-300'
                }`}
              >
                {selectedTags.includes(tag) && <Check size={14} className="text-white" />}
              </div>
            </div>
          ))}
        </div>
      )}
    </MobileFullScreenModal>
  );
}

interface MobileTaskUserFilterProps {
  isOpen: boolean;
  onClose: () => void;
  userFilters: UserFilter[];
  toggleUserFilter: (userId: string, isExclusive?: boolean) => void;
}

/**
 * Mobile user filter dropdown for tasks
 * Displays user/assignee filters in a full-screen modal
 */
export function MobileTaskUserFilter({
  isOpen,
  onClose,
  userFilters,
  toggleUserFilter,
}: MobileTaskUserFilterProps) {
  if (!isOpen) return null;

  return (
    <MobileFullScreenModal
      isOpen={isOpen}
      onClose={onClose}
      title="Select User"
      footerContent={<ModalDoneFooter onClose={onClose} />}
    >
      {/* Users section */}
      <div>
        <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
          <h3 className="font-medium text-gray-700">People</h3>
          {userFilters.some(f => f.active) && (
            <button
              onClick={e => {
                e.stopPropagation();
                userFilters.forEach(filter => {
                  if (filter.active) toggleUserFilter(filter.id, true);
                });
              }}
              className="text-blue-400 hover:text-blue-600 transition-colors"
              aria-label="Clear user selection"
            >
              <X size={16} />
            </button>
          )}
        </div>
        {userFilters.map(filter => (
          <div
            key={filter.id}
            className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-[var(--content-bg-color)] cursor-pointer"
            onClick={() => toggleUserFilter(filter.id, true)}
          >
            <div className="flex-1 flex items-center gap-3">
              {filter.avatar_url ? (
                <img
                  src={filter.avatar_url}
                  alt={filter.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
                  {filter.name?.charAt(0) || '?'}
                </div>
              )}
              <span className="font-medium text-base">{filter.name}</span>
            </div>
            <div
              className={`w-5 h-5 flex items-center justify-center rounded-full border ${
                filter.active ? 'bg-green-500 border-green-500' : 'border-gray-300'
              }`}
            >
              {filter.active && <Check size={14} className="text-white" />}
            </div>
          </div>
        ))}
      </div>
    </MobileFullScreenModal>
  );
}
